// src/pages/TruckHDStyleDetail.jsx
import React, { useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { TRUCK_HD_STYLES } from "../data/truckHdStyles";
import MediaLightbox from "../components/MediaLightbox";

/* ---------- CRA/Webpack gallery loader (per slug, tolerant of missing folders) ---------- */
function importAll(ctx) {
  return ctx
    .keys()
    .sort() // 01, 02, 03 ... 10 (use leading zeros for order)
    .map((key) => {
      const file = key.replace("./", "");
      const isVideo = /\.(mp4|mov|webm)$/i.test(file);
      return { type: isVideo ? "video" : "image", src: ctx(key) };
    });
}

/**
 * IMPORTANT:
 * CRA/Webpack require.context cannot be fully dynamic.
 * So we register known slugs here. When you add a new Truck HD style slug,
 * add one new try{} line below (copy/paste).
 *
 * Folder pattern:
 *   src/assets/samples/truckhd/<slug>/
 */
const SAMPLE_CONTEXTS = {};
try {
  SAMPLE_CONTEXTS.ts1 = require.context(
    "../assets/samples/truckhd/ts1",
    false,
    /\.(png|jpe?g|webp|mp4|mov|webm)$/i
  );
} catch {}
try {
  SAMPLE_CONTEXTS.ts2 = require.context(
    "../assets/samples/truckhd/ts2",
    false,
    /\.(png|jpe?g|webp|mp4|mov|webm)$/i
  );
} catch {}
try {
  SAMPLE_CONTEXTS.ts3 = require.context(
    "../assets/samples/truckhd/ts3",
    false,
    /\.(png|jpe?g|webp|mp4|mov|webm)$/i
  );
} catch {}
try {
  SAMPLE_CONTEXTS.ts4 = require.context(
    "../assets/samples/truckhd/ts4",
    false,
    /\.(png|jpe?g|webp|mp4|mov|webm)$/i
  );
} catch {}

export default function TruckHDStyleDetail() {
  const { styleSlug } = useParams();
  const navigate = useNavigate();

  const style = TRUCK_HD_STYLES.find((s) => s.slug === styleSlug);

  const samples = useMemo(() => {
    const ctx = SAMPLE_CONTEXTS[styleSlug];
    return ctx ? importAll(ctx) : [];
  }, [styleSlug]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openAt = useCallback((i) => {
    setActiveIndex(i);
    setLightboxOpen(true);
  }, []);
  const close = useCallback(() => setLightboxOpen(false), []);
  const prev = useCallback(
    () => setActiveIndex((i) => (i - 1 + samples.length) % samples.length),
    [samples.length]
  );
  const next = useCallback(
    () => setActiveIndex((i) => (i + 1) % samples.length),
    [samples.length]
  );

  if (!style) {
    return (
      <section className="max-w-5xl mx-auto px-4 py-20">
        <p className="text-neutral-600 mb-4">Style not found.</p>
        <button
          onClick={() => navigate("/wheels/truck-hd")}
          className="px-4 py-2 rounded bg-black text-white"
        >
          ← Back to Truck HD Wheels
        </button>
      </section>
    );
  }

  return (
    <section className="max-w-6xl mx-auto px-4 py-12">
      <Link
        to="/wheels/truck-hd"
        className="text-sm text-neutral-500 hover:text-neutral-800"
      >
        ← Back to Truck HD Wheels
      </Link>

      {/* Top hero section (same layout you already have) */}
      <div className="grid md:grid-cols-2 gap-10 mt-6">
        {/* Image */}
        <div className="rounded-3xl overflow-hidden bg-neutral-100 border border-neutral-200">
          <img
            src={style.img}
            alt={style.name}
            className="w-full h-full object-contain bg-white"
            loading="lazy"
          />
        </div>

        {/* Content */}
        <div>
          <h1 className="text-3xl font-bold">{style.name}</h1>
          <p className="mt-3 text-neutral-700">{style.blurb}</p>

          <h3 className="mt-6 font-semibold">Specifications</h3>
          <ul className="mt-3 space-y-2 text-sm text-neutral-700 list-disc pl-5">
            {(style.specs || []).map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/build-your-custom-wheels"
              className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold bg-black text-white hover:bg-neutral-900"
            >
              Request a Quote
            </Link>
            <Link
              to="/wheels/truck-hd"
              className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold border border-neutral-300 hover:bg-neutral-50"
            >
              View Other Truck HD Styles
            </Link>
          </div>

          <p className="mt-3 text-xs text-neutral-500">
            Built to order. Final specs depend on your truck, suspension, and
            tire setup.
          </p>
        </div>
      </div>

      {/* ✅ NEW: Lookbook grid under hero (like your monoblock pages) */}
      {samples.length > 0 ? (
        <>
          <h3 className="mt-12 mb-3 text-xl font-semibold tracking-tight">
            Lookbook — real builds & samples
          </h3>
          <p className="mb-6 text-sm text-neutral-600">
            Click any image or video to view full screen. Use ◀ ▶ keys, or Esc
            to close.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {samples.map((m, i) => (
              <button
                key={m.src}
                onClick={() => openAt(i)}
                className="group relative overflow-hidden rounded-xl bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                aria-label="Open sample"
              >
                {m.type === "image" ? (
                  <img
                    src={m.src}
                    alt=""
                    className="h-36 w-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-36 w-full overflow-hidden relative">
                    <video
                      src={m.src}
                      className="h-full w-full object-cover"
                      muted
                      loop
                      playsInline
                      autoPlay
                    />
                    <span className="absolute bottom-2 right-2 text-[11px] bg-black/60 text-white px-2 py-0.5 rounded">
                      video
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>

          {lightboxOpen && (
            <MediaLightbox
              items={samples}
              index={activeIndex}
              onClose={close}
              onPrev={prev}
              onNext={next}
            />
          )}
        </>
      ) : (
        <p className="mt-12 text-sm text-neutral-500">
          No samples for{" "}
          <span className="font-semibold">{styleSlug}</span> yet. Add files to{" "}
          <code className="mx-1">src/assets/samples/truckhd/{styleSlug}/</code>{" "}
          and restart the dev server.
        </p>
      )}
    </section>
  );
}
